import { AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import {
  type CreditAnalysis,
  CreditAnalysisStatus as CreditAnalysisStatusEnum,
} from '@/api/commercial/credit-analysis';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { useCreditAnalysisUpdateForm } from './use-credit-analysis-update.form';

const SITUATION_OPTIONS = [
  { value: 'APPROVED', label: 'Aprovada' },
  { value: 'PARTIALLY_APPROVED', label: 'Aprovada com ressalva' },
  { value: 'DECLINED', label: 'Reprovada' },
];

const PAYMENT_METHODS = [
  { value: 'SEM_RESTRICAO', label: 'Sem restrição' },
  { value: 'APENAS_A_VISTA', label: 'Somente à vista' },
];

export function CreditAnalysisUpdateForm(props: {
  creditAnalysis: CreditAnalysis | null;
  onSuccess?: () => void;
  allDocumentsReviewed?: boolean;
}) {
  const {
    situation,
    paymentMethod,
    creditLimit,
    newAnalysisIn,
    nextPurchase,
    observations,
    isUnlimited,

    setSituation,
    setPaymentMethod,
    setCreditLimit,
    setNewAnalysisIn,
    setNextPurchase,
    setObservations,
    setIsUnlimited,

    isApproved,
    isDeclined,
    canSubmit,
    isSubmitting,

    handleApprove,
    handleDecline,
    reset,
  } = useCreditAnalysisUpdateForm(props);

  const { creditAnalysis } = props;

  const isWaitingForClientCorrection =
    creditAnalysis?.status === CreditAnalysisStatusEnum.PENDING_EXTERNAL_DOCS;

  const verdictIcon =
    situation === 'DECLINED' ? (
      <XCircle className="h-6 w-6 text-red-600" />
    ) : situation ? (
      <CheckCircle2 className="h-6 w-6 text-green-600" />
    ) : null;

  const situationOptions = SITUATION_OPTIONS;
  const paymentMethodOptions = PAYMENT_METHODS;

  return (
    <Card>
      <CardTitle className="px-5 pt-6 flex items-center justify-between">
        <span className="flex items-center gap-2">
          {verdictIcon}
          Análise
        </span>
      </CardTitle>

      <CardContent className="pt-6 space-y-6">
        {isWaitingForClientCorrection && (
          <Alert className="border-yellow-200 bg-yellow-50 text-yellow-900">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertTitle>Aguardando correção de documentos</AlertTitle>
            <AlertDescription>Aguardando correção de documentos pelo cliente.</AlertDescription>
          </Alert>
        )}

        {/* Linha 1 */}
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-2">
            <Label>Situação</Label>
            <Combobox
              data={situationOptions}
              keyExtractor={(i) => i.value}
              displayValueGetter={(i) => i.label}
              type="single"
              value={
                situation
                  ? [
                      {
                        value: situation,
                        label:
                          situationOptions.find((s) => s.value === situation)?.label || situation,
                      },
                    ]
                  : []
              }
              onOptionChange={(selected) => setSituation((selected[0]?.value as any) ?? null)}
              placeholder="Selecione a situação"
            />
          </div>

          {isApproved && (
            <div className="flex flex-col gap-2">
              <Label>Formas de Pagamento</Label>
              <Combobox
                data={paymentMethodOptions}
                keyExtractor={(i) => i.value}
                displayValueGetter={(i) => i.label}
                type="single"
                value={
                  paymentMethod
                    ? [
                        {
                          value: paymentMethod,
                          label:
                            paymentMethodOptions.find((m) => m.value === paymentMethod)?.label ||
                            paymentMethod,
                        },
                      ]
                    : []
                }
                onOptionChange={(selected) => setPaymentMethod(selected[0]?.value ?? null)}
                placeholder="Selecione a forma de pagamento"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label>Próxima Análise</Label>

            {!nextPurchase && (
              <Input
                type="date"
                value={newAnalysisIn}
                onChange={(e) => setNewAnalysisIn(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            )}

            <div className="flex items-center gap-2 mt-1">
              <Switch checked={nextPurchase} onCheckedChange={setNextPurchase} />
              <span className="text-sm text-muted-foreground">Na próxima venda</span>
            </div>
          </div>
        </div>

        {/* Linha 2 */}
        {isApproved && (
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <div className="flex flex-col gap-2">
              <Label>Limite</Label>

              <CurrencyInput
                value={creditLimit || '0,00'}
                onChange={(value) => {
                  setCreditLimit(value);
                  if (value && value !== '0,00' && isUnlimited) {
                    setIsUnlimited(false);
                  }
                }}
                disabled={isUnlimited}
              />

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={isUnlimited}
                  onCheckedChange={(checked: boolean) => {
                    const val = checked === true;
                    setIsUnlimited(val);
                    if (val) setCreditLimit('');
                  }}
                />
                <Label>Ilimitado</Label>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label>{isDeclined ? 'Motivo da Reprovação' : 'Observações'} *</Label>

              <Textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={4}
              />
            </div>
          </div>
        )}

        {!isApproved && (
          <div className="flex flex-col gap-2">
            <Label>{isDeclined ? 'Motivo da Reprovação' : 'Observações'} *</Label>

            <Textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              rows={4}
            />
          </div>
        )}

        {/* Ações */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={reset} disabled={isSubmitting}>
            Limpar
          </Button>

          {isDeclined ? (
            <Button
              onClick={handleDecline}
              disabled={isSubmitting || !canSubmit}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? 'Salvando...' : 'Reprovar Análise'}
            </Button>
          ) : (
            <Button
              onClick={handleApprove}
              disabled={isSubmitting || !canSubmit || !isApproved}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? 'Salvando...' : 'Aprovar Análise'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
